import React from 'react';
import clsx from 'clsx';

/**
 * The card that opens a detail page: a picture, the title with its badges, a
 * line of facts, and the actions on the right. From `SellerDetail`, where
 * `ZoneDetail` copied it.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.media] - An icon or a logo, in the square tile.
 * @param {string} [props.mediaClassName] - Replaces the tile's green gradient,
 *   e.g. a white ground for a logo.
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.badges] - Next to the title.
 * @param {React.ReactNode} [props.meta] - Short facts under the title.
 * @param {React.ReactNode} [props.actions]
 * @param {string} [props.testId='detail-hero']
 */
export const DetailHero = ({
  media,
  mediaClassName = 'bg-gradient-to-br from-ninja-greenLight to-ninja-green/20 border-ninja-green/30 text-ninja-green',
  title,
  badges,
  meta,
  actions,
  testId = 'detail-hero',
}) => (
  <section
    data-testid={testId}
    className="bg-white dark:bg-[#1a1a1a] rounded-3xl p-5 sm:p-8 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.1)] border border-border relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
  >
    <div className="absolute top-0 right-0 w-64 h-64 bg-ninja-green/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />

    {/* Stacks on a phone: next to a 96px tile, the title had no room left and was cut. */}
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 relative z-10 min-w-0 w-full md:w-auto">
      {media && (
        <div
          className={clsx(
            'w-16 h-16 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center border shadow-inner flex-shrink-0 overflow-hidden',
            mediaClassName
          )}
        >
          {media}
        </div>
      )}
      <div className="min-w-0 max-w-full">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-ninja-dark dark:text-white tracking-tight break-words min-w-0">
            {title}
          </h1>
          {badges}
        </div>
        {meta && (
          <div className="text-sm text-text-secondary flex flex-wrap items-center gap-x-4 gap-y-1">
            {meta}
          </div>
        )}
      </div>
    </div>

    {actions && (
      <div className="relative z-10 flex flex-col sm:flex-row gap-3 w-full md:w-auto">{actions}</div>
    )}
  </section>
);
